import { useState, type FC, type JSX } from 'react';
import { 
    CheckCircle, 
    ChevronRight, 
    Clock, 
    Phone, 
    Search,
    Loader2
} from 'lucide-react';
import CallDetailsModal from './CallDetailsModal';
import { AllCall } from '../../services/api/calls/callsApi';
import { PaginationType } from '../../types';
import Pagination from '../shared/Pagination';

interface CallHistoryTabProps {
    calls: AllCall[] | undefined;
    pagination?: PaginationType;
    isLoading?: boolean;
    currentPage: number;
    onPageChange: (page: number) => void;
    searchTerm: string | undefined;
    setSearchTerm: (term: string | undefined) => void;
}

const CallHistoryTab: FC<CallHistoryTabProps> = ({ 
  calls, 
  pagination, 
  isLoading, 
  currentPage, 
  onPageChange, 
  searchTerm, 
  setSearchTerm 
}): JSX.Element => {
    const [selectedCall, setSelectedCall] = useState<AllCall | null>(null);

  // const filteredCalls =   calls ? calls.filter(call =>
  //   call.user_name.toLowerCase().includes(searchTerm.toLowerCase()) 
  // ).sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime()) : [];

  // const getUrgencyColor = (urgency: string) => {
  //   switch (urgency) {
  //     case 'emergency': return 'bg-red-100 text-red-800';
  //     case 'high': return 'bg-orange-100 text-orange-800';
  //     case 'medium': return 'bg-yellow-100 text-yellow-800';
  //     case 'low': return 'bg-green-100 text-green-800';
  //     default: return 'bg-gray-100 text-gray-800';
  //   }
  // };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'text-green-600';
      case 'ended': return 'text-gray-600';
      case 'dialing': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search calls by patient name or purpose..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
      </div>

      {/* Call List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      ) : (
      <div className="space-y-3">
        {calls && calls.map((call) => (
          <div
            key={call.id}
            onClick={() => setSelectedCall(call)}
            className="border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition-all cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className={`p-3 rounded-full bg-blue-100`}>
                  <Phone className={`w-5 h-5 text-blue-600`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{call.user_name}</h3>
                    {/* <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getUrgencyColor(call.urgencyLevel)}`}>
                      {call.urgencyLevel}
                    </span> */}
                      <span className="text-xs text-blue-600 font-medium">Inbound</span>
                  </div>
                  {/* <p className="text-sm text-gray-600 capitalize mb-2">{call.reason}</p> */}
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(call.start_time).toLocaleString()}
                    </span>
                    {call.duration_seconds && (
                      <span>{Math.floor(call.duration_seconds / 60)}m {call.duration_seconds % 60}s</span>
                    )}
                    <span className={`flex items-center ${getStatusColor(call.status)}`}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {call.status}
                    </span>
                  </div>
                  {/* {call.aiSummary && (
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <Bot className="w-4 h-4 text-blue-600 mt-0.5" />
                        <p className="text-sm text-blue-900">{call.aiSummary}</p>
                      </div>
                    </div>
                  )} */}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        ))}

        {calls?.length === 0 && (
          <div className="text-center py-12">
            <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No calls found</p>
          </div>
        )}
      </div>
      )}

      {/* Pagination Controls */}
      {pagination && !isLoading && (
        <Pagination
          pagination={pagination}
          currentPage={currentPage}
          onPageChange={onPageChange}
          itemName="calls"
        />
      )}

      {/* Call Details Modal */}
      {selectedCall && (
        <CallDetailsModal call={selectedCall} onClose={() => setSelectedCall(null)} />
      )}
    </div>
  ); 
}

export default CallHistoryTab;